/**
 * Utility functions for building webshop URLs
 */

/**
 * Get the webshop base URL from environment variables
 */
export const getWebshopBaseUrl = (): string => {
  return import.meta.env.VITE_WEBSHOP_URL || 'http://localhost:3102';
};

/**
 * Build a webshop URL for a specific event
 * Pattern: {baseUrl}/o/{orgSlug}/events/{eventSlug}
 *
 * @param orgSlug - Organization slug
 * @param eventSlug - Event slug
 * @returns Full webshop URL for the event
 */
export const buildEventWebshopUrl = (orgSlug: string, eventSlug: string): string => {
  const baseUrl = getWebshopBaseUrl();
  return `${baseUrl}/o/${orgSlug}/events/${eventSlug}`;
};

/**
 * Build a webshop URL for a specific product in an event
 * Pattern: {baseUrl}/o/{orgSlug}/events/{eventSlug}/products/{productId}
 *
 * @param orgSlug - Organization slug
 * @param eventSlug - Event slug
 * @param productId - Product/Item ID (optional)
 * @returns Full webshop URL for the product
 */
export const buildProductWebshopUrl = (
  orgSlug: string,
  eventSlug: string,
  productId?: number | string
): string => {
  const baseUrl = buildEventWebshopUrl(orgSlug, eventSlug);
  if (productId) {
    return `${baseUrl}/products/${productId}`;
  }
  return baseUrl;
};

/**
 * Build a webshop URL for a specific item's add-ons
 * Pattern: {baseUrl}/o/{orgSlug}/events/{eventSlug}/items/{itemId}/addons
 *
 * @param orgSlug - Organization slug
 * @param eventSlug - Event slug
 * @param itemId - Item ID
 * @returns Full webshop URL for the item's add-ons section
 */
export const buildItemAddonsWebshopUrl = (
  orgSlug: string,
  eventSlug: string,
  itemId: number | string
): string => {
  const baseUrl = buildEventWebshopUrl(orgSlug, eventSlug);
  return `${baseUrl}/items/${itemId}/addons`;
};
